import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import TopNav from "@/components/TopNav";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, MoreVertical, Puzzle, ToggleRight, FolderOpen, CheckCircle2, MousePointerClick, Info } from "lucide-react";

const steps = [
  {
    icon: MoreVertical,
    title: "Open Chrome Menu",
    description: 'Click the **3 dots** (⋮) at the **top right corner** of your Chrome browser.',
  },
  {
    icon: Puzzle,
    title: "Go to Extensions",
    description: 'Navigate to **Extensions → Manage Extensions**.',
  },
  {
    icon: ToggleRight,
    title: "Enable Developer Mode",
    description: 'Toggle **Developer Mode** on at the **top right** of the Extensions page.',
  },
  {
    icon: MousePointerClick,
    title: 'Click "Load unpacked"',
    description: 'Click the **"Load unpacked"** button that appears at the top left.',
  },
  {
    icon: FolderOpen,
    title: "Select Extension Folder",
    description: 'Select the **extracted extension folder** you downloaded earlier.',
  },
  {
    icon: CheckCircle2,
    title: "Extension Installed",
    description: 'The **AI Translator App** extension will now appear in your extensions list.',
  },
  {
    icon: Puzzle,
    title: "Open Extensions Menu",
    description: 'Click the **Extensions icon** (puzzle piece) in the browser toolbar.',
  },
  {
    icon: MousePointerClick,
    title: "Start Using It",
    description: 'Select **AI Translator App** to start using it!',
  },
];

function renderMarkdownBold(text: string) {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="text-foreground font-semibold">
        {part}
      </strong>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

const ExtensionInstallationGuide = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <TopNav />
      <main className="flex-1 container mx-auto px-4 py-8 sm:py-12 max-w-3xl">
        {/* Back button */}
        <Link to="/">
          <Button variant="ghost" size="sm" className="mb-6 gap-2 text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
            {t('guide.backToHome')}
          </Button>
        </Link>

        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 px-4 py-1.5 text-sm font-medium text-primary mb-4">
            <Puzzle className="h-4 w-4" />
            Chrome Extension
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3">
            {t('guide.title')}
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto">
            {t('guide.subtitle')}
          </p>
        </div>

        {/* Download reminder */}
        <div className="mb-8 p-4 rounded-xl border border-primary/20 bg-primary/5 flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
          <Download className="h-5 w-5 text-primary shrink-0" />
          <div className="text-center sm:text-left flex-1">
            <p className="text-sm font-medium text-foreground">{t('guide.downloadFirst')}</p>
            <p className="text-xs text-muted-foreground">{t('guide.downloadFirstDesc')}</p>
          </div>
          <a
            href="https://drive.google.com/drive/folders/1YDe798n1uIyaNXV2ss7RMIM12ZcvaqeF?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button size="sm" className="whitespace-nowrap">
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {t('footer.addToChrome')}
            </Button>
          </a>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={index}
                className="flex gap-4 p-4 sm:p-5 rounded-xl border border-border/60 bg-card/50 hover:bg-card/80 transition-colors"
              >
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary font-bold text-sm border border-primary/20">
                    {index + 1}
                  </div>
                  {index < steps.length - 1 && (
                    <div className="w-px flex-1 bg-border/50 min-h-[8px]" />
                  )}
                </div>
                <div className="pt-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="h-4 w-4 text-primary" />
                    <h3 className="font-semibold text-foreground text-sm sm:text-base">{step.title}</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {renderMarkdownBold(step.description)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Note */}
        <div className="mt-8 p-4 rounded-xl border border-border/50 bg-muted/30 flex gap-3">
          <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground mb-1">{t('guide.noteTitle')}</p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t('guide.noteDesc')}
            </p>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 text-center">
          <p className="text-muted-foreground text-sm mb-3">{t('guide.needHelp')}</p>
          <Link to="/contact">
            <Button variant="outline" size="sm">
              {t('footer.contactSupport')}
            </Button>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ExtensionInstallationGuide;
